const STATE_PREFIX = 'persist:'
const STATE_KEY_CONFIG = 'persist:config'

export const STATE_MIGRATION_KEYS = ['appState', 'attendant', 'config', 'order', 'peripheral', 'revenueCenter']

const deserializeNestedData = (serializedData) => {
  try {
    const output = {}
    const deserializedData = JSON.parse(serializedData)

    for (const key in deserializedData) {
      try {
        const parsedData = JSON.parse(deserializedData?.[key])
        output[key] = parsedData
      } catch (err) {
        output[key] = deserializedData?.[key]
      }
    }

    return output
  } catch (error) {
    console.error('Error deserializing data from localStorage:', error)
    return null
  }
}

/**
 * Fetch and parse data from localStorage by key
 *
 * @param {String} key
 * @returns
 */
export const deserializeFromLocalStorage = (key) => {
  const serializedData = localStorage.getItem(key)
  if (serializedData === null) {
    return null // Handle case where no data is found
  }
  return deserializeNestedData(serializedData)
}

/**
 * Hydrate state data from localStorage by key
 *
 * @param {String} key
 * @returns
 */
export const dataMigrationByKey = (key, state) => {
  return {
    ...state,
    ...deserializeFromLocalStorage(STATE_PREFIX + key),
  }
}

/**
 * Migration for individual states then dispatches the rehydrate action
 * Clears localStorage after migration
 *
 * @param {Object} store
 * @param {String} key
 */
const migrateNestedState = (store, key) => {
  if (!localStorage.getItem(STATE_PREFIX + key)) {
    return
  }

  const persistedState = store.getState()

  const payload = dataMigrationByKey(key, persistedState[key])
  store.dispatch({ type: 'persist/REHYDRATE', payload, key })

  // Clear localStorage
  localStorage.removeItem(STATE_PREFIX + key)
}

/**
 * Rehydrate store data from localStorage and unsets it from localStorage
 *
 * @param {Object} store
 */
export const hydrateStoreData = (store) => {
  // only do migration if config state exists in localStorage
  if (!localStorage.getItem(STATE_KEY_CONFIG)) {
    return
  }

  // migrate individual states
  for (let key of STATE_MIGRATION_KEYS) {
    migrateNestedState(store, key)
  }

  store.dispatch({ type: 'REFRESH_CONFIG' })
}

/**
 * Migration for individual states then dispatches the rehydrate action
 * Clears indexedDB after migration
 *
 * @param {} dbRequest.transaction.objectStore
 * @param {Object} store
 * @param {String} key
 */
const migrateNestedObjectStores = (objectStore, store, key) => {
  const request = objectStore.get(STATE_PREFIX + key)

  request.onerror = (event) => {
    console.error('Error fetching data from objectStore:', event.target.errorCode)
  }

  request.onsuccess = (event) => {
    const serializedData = event.target.result
    if (serializedData === undefined) {
      return
    }

    const persistedState = store.getState()
    const payload = dataMigrationByKey(key, {
      ...(persistedState[key] ?? {}),
      ...deserializeNestedData(serializedData),
    })

    if (key === 'order') {
      alert(JSON.stringify(deserializeNestedData(serializedData)))
    }
    store.dispatch({ type: 'persist/REHYDRATE', payload, key })

    // Clear indexedDB
    const deleteRequest = objectStore.delete(STATE_PREFIX + key)
    deleteRequest.onerror = (event) => {
      console.error('Error deleting data from indexedDB:', event.target.errorCode)
    }
  }

  // for simulated request tests
  if (request.isTestError) {
    request.onerror(request.isTestError)
  }
  if (request.isTestSuccess) {
    request.onsuccess(request.isTestSuccess)
  }
}

/**
 * Rollback stored data back to localStorage and from indexedDB
 *
 * @param {Object} store
 */
export const rollbackStorageMigration = (store) => {
  // check if indexedDB ObjectStore exists
  const dbRequest = indexedDB.open('VN_PERSIST_STORE')
  dbRequest.onupgradeneeded = (event) => {
    console.warn('Database does not exist or is being upgraded')
    event.target.transaction.abort() // prevent creating the database
  }

  dbRequest.onerror = (event) => {
    console.error('Error opening indexedDB:', event.target.errorCode)
  }

  dbRequest.onsuccess = (event) => {
    const db = event.target.result
    let transaction
    try {
      transaction = db.transaction(['keyvaluepairs'], 'readwrite')
    } catch (error) {
      console.error('Error creating transaction:', error)
      return // Stop execution if objectStore does not exist
    }
    const objectStore = transaction.objectStore('keyvaluepairs')

    // IndexedDB exists and has data
    try {
      // rollback data from indexedDB
      for (let key of STATE_MIGRATION_KEYS) {
        migrateNestedObjectStores(objectStore, store, key)
      }
    } catch (error) {
      console.error('Error rolling back migration:', error)
    }

    // Delete the database after migration
    const deleteDbRequest = indexedDB.deleteDatabase('VN_PERSIST_STORE')
    deleteDbRequest.onerror = (event) => {
      console.error('Error deleting indexedDB:', event.target.errorCode)
    }
    deleteDbRequest.onsuccess = () => {
      console.warn('IndexedDB deleted successfully')
    }

    store.dispatch({ type: 'REFRESH_CONFIG' })
  }

  // for simulated db tests
  if (dbRequest.isTestError) {
    dbRequest.onerror(dbRequest.isTestError)
  }
  if (dbRequest.isTestSuccess) {
    dbRequest.onsuccess(dbRequest.isTestSuccess)
  }
}
